import ModalDialog from '@ui/modal/ModalDialog';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SwiperCore, { EffectFade, Keyboard, Navigation, Thumbs, FreeMode, Pagination } from 'swiper';
import { Swiper as SwiperInstance, SwiperSlide } from 'swiper/react';
import imageutils, { XS, SM, LG, MD } from '@utils/imageutils';
import { appPostStructuredMessage } from '@common/utils/appUtils';
import ProductGalleryImage from './ProductGalleryImage';
import useBreakpoint from '@common/utils/useBreakpoint';
import ProductVideo from './ProductVideo';
import classNames from 'classnames';
SwiperCore.use([Keyboard, Navigation, EffectFade, Thumbs, FreeMode, Pagination]);
const ProductGallery = (props) => {
    const { t } = useTranslation();
    const [swiperInstance, setSwiperInstance] = useState();
    const [thumbsSwiper, setThumbsSwiper] = useState(null);
    const [activeIndex, setActiveIndex] = useState(0);
    const [productUrl, setProductUrl] = useState();
    const [isBeginning, setIsBeginning] = useState(true);
    const [isEnd, setIsEnd] = useState(false);
    const currentBreakpoint = useBreakpoint();
    const handleClose = () => {
        setThumbsSwiper(null);
        props.onRequestClose();
    };
    const gapMap = {
        0: 4.7,
        375: 4.7,
        600: 4,
        1024: 8,
        1200: 16,
        1440: 16,
    };
    const sizeThumbsMap = {
        0: XS,
        375: XS,
        600: SM,
        1024: XS,
        1200: XS,
        1440: XS,
    };
    const sizeMainMap = {
        0: MD,
        375: MD,
        600: MD,
        1024: LG,
        1200: LG,
        1440: LG,
    };
    //dev level to debug with video on sandbox
    // const media: string[] = [
    //   'https://moncler-cdn.thron.com/delivery/public/video/moncler/H209Z1A00001M2904999_V2/dpx6uv/WEBFULLHD/H209Z1A00001M2904999_V2.mp4',
    //   'https://moncler-cdn.thron.com/delivery/public/video/moncler/H209Z1A00001M2904999_V1/dpx6uv/WEBFULLHD/H209Z1A00001M2904999_V1.mp4',
    //   'https://moncler-cdn.thron.com/delivery/public/image/moncler/H209Z1A00001M2904999_X/dpx6uv/std/0x0/H209Z1A00001M2904999_X.jpg',
    //   'https://staging2.cdn.moncler.ows.farm/on/demandware.static/-/Library-Sites-library-global/default/dw400a89ed/mbm/3-vion-special70-v2.jpg',
    //   'https://staging2.cdn.moncler.ows.farm/on/demandware.static/-/Library-Sites-library-global/default/dw7335a406/mbm/3-vion-iconic-v2.jpg',
    //   'https://staging2.cdn.moncler.ows.farm/on/demandware.static/-/Library-Sites-library-global/default/dw7335a406/mbm/3-vion-iconic-v2.jpg',
    //   'https://staging2.cdn.moncler.ows.farm/on/demandware.static/-/Library-Sites-library-global/default/dw7335a406/mbm/3-vion-iconic-v2.jpg',
    //   'https://staging2.cdn.moncler.ows.farm/on/demandware.static/-/Library-Sites-library-global/default/dw7335a406/mbm/3-vion-iconic-v2.jpg',
    // ];
    const media = props.media;
    // @ts-ignore
    const direction = useMemo(() => (currentBreakpoint >= window.mqObj.md ? 'vertical' : 'horizontal'), [currentBreakpoint]);
    const slides = useMemo(() => {
        //@ts-ignore -- ignoring breakpoint mismatch
        return currentBreakpoint >= window.mqObj.md ? 5 : 5.5;
    }, [currentBreakpoint]);
    const paginationMessage = t('carousel.paginationbulletmessage');
    useEffect(() => {
        var _a, _b;
        // set productUrl in product gallery Minibag / Bag
        let variationColor = (_b = (_a = props.product) === null || _a === void 0 ? void 0 : _a.variationAttributes) === null || _b === void 0 ? void 0 : _b.find((va) => va.id === 'color');
        let selectedValue = variationColor === null || variationColor === void 0 ? void 0 : variationColor.values.find((v) => v.value === variationColor.value);
        let prodUrl = selectedValue === null || selectedValue === void 0 ? void 0 : selectedValue.productUrl;
        setProductUrl(prodUrl);
    }, [props.product]);
    const updateProgressThumb = (swiper) => {
        setIsBeginning(swiper.isBeginning);
        setIsEnd(swiper.isEnd);
    };
    const appPdPLink = () => {
        appPostStructuredMessage('pdpLink', { pid: props.product.id });
    };
    return (React.createElement(ModalDialog, { noPadding: true, hideHeader: true, className: "product-zoom-modal focus-inverted", isOpen: typeof props.isOpen === 'number', onRequestClose: handleClose, contentLabel: "gallery", overlayClassName: "mobile-visible", onAfterClose: () => {
            if (props.scrollHeight) {
                window.scrollTo(0, props.scrollHeight);
            }
        } },
        props.showPDPLink &&
            (window.appmode ? (React.createElement("button", { type: "button", className: "product-gallery__link", onClick: appPdPLink },
                React.createElement("p", null, t('carousel.gotoproductpage')))) : (React.createElement("a", { href: props.product.productUrl || productUrl, className: "product-gallery__link" },
                React.createElement("p", null, t('carousel.gotoproductpage'))))),
        React.createElement(SwiperInstance, { onSwiper: (swipr) => {
                setSwiperInstance(swipr);
            }, onSlideChange: (swiper) => {
                setActiveIndex(swiper.activeIndex);
            }, keyboard: true, direction: direction, navigation: {
                nextEl: '.carousel-gallery__arrow--arrow-right',
                prevEl: '.carousel-gallery__arrow--arrow-left',
            }, mousewheel: {
                forceToAxis: true,
                thresholdDelta: 20,
            }, allowTouchMove: false, className: "product-zoom swiper-container swiper-container--gallery", effect: "fade", initialSlide: typeof props.isOpen === 'number' ? props.isOpen : 0, speed: 1000, thumbs: { swiper: thumbsSwiper }, loop: true, loopPreventsSlide: false, slidesPerView: 1 }, media === null || media === void 0 ? void 0 : media.map((url, index) => {
            var _a, _b, _c;
            return (React.createElement(SwiperSlide, { key: `${(_a = props.product) === null || _a === void 0 ? void 0 : _a.id}${index}` },
                React.createElement(ProductGalleryImage, { index: index, media: media, activateIndex: activeIndex, src: imageutils.transform(url, sizeMainMap[currentBreakpoint], (_c = (_b = props.product) === null || _b === void 0 ? void 0 : _b.imgs) === null || _c === void 0 ? void 0 : _c.alt, null, 'avif'), isZoom: true }),
                React.createElement("div", { className: "swiper-lazy-preloader" })));
        })),
        React.createElement("div", { className: "product-zoom-thumbs--wrapper" },
            currentBreakpoint >= window.mqObj.md && (React.createElement("button", { className: "product-zoom-thumbs__navbutton product-zoom-thumbs__navbutton-prev", onClick: () => thumbsSwiper === null || thumbsSwiper === void 0 ? void 0 : thumbsSwiper.slidePrev(), disabled: isBeginning, "aria-label": t('carousel.prevslidemessage') })),
            React.createElement(SwiperInstance, { onSwiper: (swiper) => {
                    setThumbsSwiper(swiper);
                    updateProgressThumb(swiper);
                }, onTransitionStart: updateProgressThumb, onTransitionEnd: updateProgressThumb, watchOverflow: true, spaceBetween: gapMap[currentBreakpoint] || 4, direction: direction, slidesPerView: slides, watchSlidesProgress: true, modules: [Thumbs, FreeMode, Navigation], className: classNames('product-zoom-thumbs swiper-container', { start: isBeginning }, { end: isEnd }), freeMode: { enabled: true, sticky: true }, slideToClickedSlide: true, centerInsufficientSlides: true, grabCursor: true, initialSlide: typeof props.isOpen === 'number' ? props.isOpen : 0, 
                // loop
                // loopPreventsSlide={false}
                threshold: 20 }, media === null || media === void 0 ? void 0 : media.map((url, index) => {
                var _a;
                return (React.createElement(SwiperSlide, { key: `${(_a = props.product) === null || _a === void 0 ? void 0 : _a.id}${index}` },
                    React.createElement("button", { onClick: () => swiperInstance.slideTo(index + 1), className: "product-zoom-thumbs__thumb-button", "data-index": activeIndex, "aria-label": paginationMessage.replace('INDEX_NUMBER', `${index + 1}`) },
                        url.includes('_V1') ||
                            url.includes('_V2') ||
                            url.includes('_V3') ||
                            url.includes('_V4') ||
                            url.includes('_V5') ||
                            url.includes('_V6') ||
                            url.includes('_V7') ||
                            url.includes('_V8') ||
                            url.includes('_V9') ||
                            url.includes('_V10') ? (React.createElement(ProductVideo, { url: url, isThumb: true })) : (React.createElement("img", { key: url, src: imageutils.transform(url, sizeThumbsMap[currentBreakpoint], ''), alt: "", loading: "lazy" })),
                        React.createElement("div", { className: "swiper-lazy-preloader" }))));
            })),
            currentBreakpoint >= window.mqObj.md && (React.createElement("button", { className: "product-zoom-thumbs__navbutton product-zoom-thumbs__navbutton-next", onClick: () => thumbsSwiper === null || thumbsSwiper === void 0 ? void 0 : thumbsSwiper.slideNext(), disabled: isEnd, "aria-label": t('carousel.nextslidemessage') })))));
};
export default ProductGallery;
